import { useCallback, useId, useRef, useState } from "react";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * The one file picker the shell uses: drop a file on it, or click/press it to browse.
 *
 * The five upload points used to each roll their own — three raw `<input type="file">` (whose native
 * "Choose File / No file chosen" box ignores every other control's styling and broke the baseline of
 * the row it sat in) and two hidden inputs behind a button. Dropping a file worked nowhere.
 *
 * The native input stays in the DOM, hidden: it is what actually opens the file dialog, and keeping
 * it means the label/keyboard/screen-reader path is the browser's rather than something re-invented.
 */
export function FileDrop({
  onFile,
  file,
  accept,
  disabled = false,
  label = "파일을 여기에 끌어다 놓으세요",
  hint,
  compact = false,
}: {
  /** Called with the chosen file. The caller decides whether that means "upload now" or "hold it". */
  onFile: (file: File) => void;
  /** The currently held file, when the caller keeps one — shown in place of the prompt. */
  file?: File | null;
  accept?: string;
  disabled?: boolean;
  label?: string;
  hint?: string;
  /** A shorter box, for places that sit inside an already-dense panel. */
  compact?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputId = useId();
  // A counter, not a boolean: dragging over a child fires dragleave on the parent, and a boolean
  // would flicker the highlight off while the pointer is still inside the zone.
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);

  const take = useCallback((f: File | undefined | null) => {
    if (f && !disabled) onFile(f);
  }, [disabled, onFile]);

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    take(e.dataTransfer.files?.[0]);
  }

  return (
    <div
      className={`filedrop ${dragging ? "over" : ""} ${disabled ? "disabled" : ""} ${compact ? "compact" : ""}`}
      onDragEnter={(e) => {
        e.preventDefault();
        dragDepth.current += 1;
        if (!disabled) setDragging(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={(e) => {
        e.preventDefault();
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) setDragging(false);
      }}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        disabled={disabled}
        className="filedrop-input"
        onChange={(e) => {
          take(e.target.files?.[0]);
          // Let the same file be chosen twice in a row (re-uploading an edited file is the norm here).
          e.target.value = "";
        }}
      />
      <label className="filedrop-label" htmlFor={inputId}>
        <span className="filedrop-ico" aria-hidden>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
               strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 16V4" />
            <path d="m7.5 8.5 4.5-4.5 4.5 4.5" />
            <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
          </svg>
        </span>
        <span className="filedrop-text">
          {file ? (
            <>
              <b className="filedrop-name">{file.name}</b>
              <span className="filedrop-hint">{formatBytes(file.size)} · 다른 파일을 놓으면 교체됩니다</span>
            </>
          ) : (
            <>
              <b>{dragging ? "놓으면 첨부됩니다" : label}</b>
              <span className="filedrop-hint">{hint ?? "클릭해서 고를 수도 있습니다"}</span>
            </>
          )}
        </span>
      </label>
    </div>
  );
}
